import type { EvidenceProps } from './registry';

interface ActionRecord {
  id?: string;
  name?: string;
  attributionModel: string;
  category?: string;
  dataDrivenStatus?: string;
  last30dConversions: number;
}

interface ConsolidationCandidate {
  category: string;
  actionCount: number;
  totalLast30dConversions: number;
  crossesThreshold: boolean;
}

interface AttributionModelEvidenceShape {
  totalConversionActions: number;
  enabledPrimaryCount: number;
  applicableCount: number;
  ddaThreshold30d: number;
  onDdaCount: number;
  onDda: ActionRecord[];
  ddaAvailableNotSelectedCount: number;
  ddaAvailableNotSelected: ActionRecord[];
  ddaUnavailableCount: number;
  ddaUnavailable: ActionRecord[];
  notApplicableCount: number;
  notApplicable: ActionRecord[];
  consolidationCandidates: ConsolidationCandidate[];
}

const MODEL_LABEL: Record<string, string> = {
  GOOGLE_SEARCH_ATTRIBUTION_DATA_DRIVEN: 'Data-Driven',
  GOOGLE_ADS_LAST_CLICK: 'Last click',
  GOOGLE_SEARCH_ATTRIBUTION_FIRST_CLICK: 'First click',
  GOOGLE_SEARCH_ATTRIBUTION_LINEAR: 'Linear',
  GOOGLE_SEARCH_ATTRIBUTION_TIME_DECAY: 'Time decay',
  GOOGLE_SEARCH_ATTRIBUTION_POSITION_BASED: 'Position-based',
  EXTERNAL: 'External',
  UNKNOWN: 'Unknown',
  UNSPECIFIED: 'Unspecified'
};

const DDA_STATUS_LABEL: Record<string, string> = {
  AVAILABLE: 'Available',
  STALE: 'Outdated',
  EXPIRED: 'Expired',
  NEVER_GENERATED: 'Not yet built',
  UNKNOWN: 'Unknown',
  UNSPECIFIED: 'Unspecified'
};

const CATEGORY_LABEL: Record<string, string> = {
  PURCHASE: 'Purchase',
  LEAD: 'Lead',
  SIGNUP: 'Sign-up',
  PAGE_VIEW: 'Page view',
  DOWNLOAD: 'Download',
  ADD_TO_CART: 'Add to cart',
  BEGIN_CHECKOUT: 'Begin checkout',
  SUBSCRIBE_PAID: 'Paid subscription',
  PHONE_CALL_LEAD: 'Phone call lead',
  IMPORTED_LEAD: 'Imported lead',
  SUBMIT_LEAD_FORM: 'Submit lead form',
  BOOK_APPOINTMENT: 'Book appointment',
  REQUEST_QUOTE: 'Request quote',
  GET_DIRECTIONS: 'Get directions',
  OUTBOUND_CLICK: 'Outbound click',
  CONTACT: 'Contact',
  ENGAGEMENT: 'Engagement',
  STORE_VISIT: 'Store visit',
  STORE_SALE: 'Store sale',
  QUALIFIED_LEAD: 'Qualified lead',
  CONVERTED_LEAD: 'Converted lead',
  DEFAULT: 'Default',
  UNKNOWN: 'Unknown',
  UNSPECIFIED: 'Unspecified'
};

function label(map: Record<string, string>, key: string | undefined): string {
  if (!key) return '—';
  return map[key] ?? key;
}

function formatVolume(n: number): string {
  if (n === 0) return '0';
  if (n < 1) return '<1';
  if (n >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: n < 10 ? 1 : 0 });
}

export const AttributionModelEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AttributionModelEvidenceShape;

  return (
    <div className='evidence'>
      <Lead evidence={e} />
      {e.ddaAvailableNotSelectedCount > 0 && (
        <ActionableSection
          actions={e.ddaAvailableNotSelected}
          count={e.ddaAvailableNotSelectedCount}
        />
      )}
      {e.ddaUnavailableCount > 0 && (
        <UnavailableSection
          actions={e.ddaUnavailable}
          count={e.ddaUnavailableCount}
          candidates={e.consolidationCandidates}
          ddaThreshold={e.ddaThreshold30d}
        />
      )}
      {e.onDdaCount > 0 && <OnDdaSection actions={e.onDda} count={e.onDdaCount} />}
      {e.notApplicableCount > 0 && (
        <NotApplicableSection actions={e.notApplicable} count={e.notApplicableCount} />
      )}
      {e.ddaAvailableNotSelectedCount === 0 && e.ddaUnavailableCount === 0 && (
        <p className='evidence__footnote'>
          All {e.applicableCount} primary, DDA-applicable conversion actions are using Data-Driven
          Attribution.
        </p>
      )}
    </div>
  );
};

const Lead = ({ evidence: e }: { evidence: AttributionModelEvidenceShape }) => (
  <p className='evidence__lead'>
    <strong>{e.enabledPrimaryCount}</strong> enabled, primary conversion actions (
    {e.applicableCount} have applicable attribution models
    {e.notApplicableCount > 0
      ? `; ${e.notApplicableCount} excluded — store visits, offline imports, third-party tracking, etc.`
      : ''}
    ). <strong>{e.onDdaCount}</strong> on Data-Driven ·{' '}
    <strong>{e.ddaAvailableNotSelectedCount}</strong> DDA-eligible but not selected ·{' '}
    <strong>{e.ddaUnavailableCount}</strong> with DDA unavailable.
  </p>
);

const ActionableSection = ({ actions, count }: { actions: ActionRecord[]; count: number }) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      DDA-eligible but not selected
      <span className='evidence__section-tag evidence__section-tag--actionable'>
        {count} actionable
      </span>
    </h4>
    <p className='evidence__section-lead'>
      These actions have <code>data_driven_model_status: AVAILABLE</code>, meaning Google has enough
      data to support DDA — they just aren't using it. Switching the attribution model is a direct,
      structural fix.
    </p>
    <ActionTable actions={actions} />
  </section>
);

const UnavailableSection = ({
  actions,
  count,
  candidates,
  ddaThreshold
}: {
  actions: ActionRecord[];
  count: number;
  candidates: ConsolidationCandidate[];
  ddaThreshold: number;
}) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      DDA unavailable
      <span className='evidence__section-tag evidence__section-tag--context'>
        {count} contextual
      </span>
    </h4>
    <p className='evidence__section-lead'>
      These actions can't currently use DDA — typically <code>Not yet built</code> indicates Google
      hasn't built a DDA model because the action lacks sufficient conversion volume (~
      {ddaThreshold}+/30d). The structural opportunity here is upstream: consolidate low-volume
      actions to meet the threshold.
    </p>
    <ActionTable actions={actions} />
    {candidates.length > 0 && (
      <ConsolidationCandidates candidates={candidates} threshold={ddaThreshold} />
    )}
  </section>
);

const ConsolidationCandidates = ({
  candidates,
  threshold
}: {
  candidates: ConsolidationCandidate[];
  threshold: number;
}) => (
  <div className='consolidation'>
    <div className='consolidation__title'>Consolidation candidates</div>
    <ul className='consolidation__list'>
      {candidates.map(c => (
        <li
          key={c.category}
          className={
            c.crossesThreshold ? 'consolidation__item--threshold' : 'consolidation__item--below'
          }
        >
          <strong>{c.actionCount}</strong> {label(CATEGORY_LABEL, c.category)} actions sum to{' '}
          <strong>{formatVolume(c.totalLast30dConversions)}</strong> conv/30d
          {c.crossesThreshold ? (
            <span className='consolidation__tag consolidation__tag--threshold'>
              would cross ~{threshold}/30d threshold
            </span>
          ) : (
            <span className='consolidation__tag consolidation__tag--below'>
              still below ~{threshold}/30d
            </span>
          )}
        </li>
      ))}
    </ul>
  </div>
);

const OnDdaSection = ({ actions, count }: { actions: ActionRecord[]; count: number }) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      Currently on Data-Driven Attribution
      <span className='evidence__section-tag evidence__section-tag--ok'>{count} on track</span>
    </h4>
    <ActionTable actions={actions} />
  </section>
);

const NotApplicableSection = ({ actions, count }: { actions: ActionRecord[]; count: number }) => (
  <section className='evidence__section'>
    <h4 className='evidence__section-title'>
      Not applicable to attribution audit
      <span className='evidence__section-tag evidence__section-tag--context'>{count} excluded</span>
    </h4>
    <p className='evidence__section-lead'>
      These actions have attribution models that don't participate in the DDA-vs-not analysis —
      typically Store Visits, Store Sales, offline imports, or third-party call tracking (model
      reported as <code>Unknown</code>, <code>External</code>, or <code>Unspecified</code>). They're
      listed here for transparency, not as a finding.
    </p>
    <ActionTable actions={actions} />
  </section>
);

const ActionTable = ({ actions }: { actions: ActionRecord[] }) => (
  <table className='evidence__table'>
    <thead>
      <tr>
        <th>Conversion action</th>
        <th>Category</th>
        <th>Attribution model</th>
        <th>DDA status</th>
        <th className='evidence__th--right'>Conv (30d)</th>
      </tr>
    </thead>
    <tbody>
      {actions.map((action, i) => (
        <tr key={action.id ?? `${action.name ?? '(unnamed)'}-${i}`}>
          <td>
            <div>{action.name ?? '(unnamed)'}</div>
            {action.id && <div className='evidence__action-id'>#{action.id}</div>}
          </td>
          <td>{label(CATEGORY_LABEL, action.category)}</td>
          <td>{label(MODEL_LABEL, action.attributionModel)}</td>
          <td>{label(DDA_STATUS_LABEL, action.dataDrivenStatus)}</td>
          <td className='evidence__td--right'>{formatVolume(action.last30dConversions)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);
